import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box, Typography, Card, CardContent, CardActionArea, Grid, Alert, Fade,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TextField, InputAdornment, IconButton, Tooltip,
  ToggleButtonGroup, ToggleButton, Skeleton, Button, useTheme,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  ContentCopy as DuplicateIcon,
  LinkOff as UnlinkIcon,
  PersonOff as NoAcsIcon,
  HomeWork as NoVisitIcon,
  Badge as BadgeIcon,
  PersonSearch as InactiveIcon,
  CreditCardOff as NoCpfIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { saudeService } from '../../services/api';

interface ResumoQualidade {
  duplicados: { cpf: number; cns: number };
  desvinculados: number;
  semAcs: number;
  semVisitaRecente: number;
  semCpf: number;
  profissionaisInvalidos: number;
  totalCidadaos: number;
  timestamp: string;
}

interface Equipe {
  ine: string;
  nome: string;
}

interface CardConfig {
  key: string;
  label: string;
  icon: React.ElementType;
  color: string;
  getCount: (r: ResumoQualidade) => number;
  csvTipo: string;
  description: string;
  hasIneFilter: boolean;
}

const CARDS: CardConfig[] = [
  {
    key: 'duplicados', label: 'Cadastros Duplicados', icon: DuplicateIcon,
    color: '#e53935', getCount: r => r.duplicados.cpf + r.duplicados.cns,
    csvTipo: 'duplicados-cpf', description: 'Cidadaos com mesmo CPF ou CNS',
    hasIneFilter: true,
  },
  {
    key: 'desvinculados', label: 'Sem Equipe', icon: UnlinkIcon,
    color: '#f57c00', getCount: r => r.desvinculados,
    csvTipo: 'desvinculados', description: 'Cidadaos sem vinculacao com equipe APS',
    hasIneFilter: false,
  },
  {
    key: 'semcpf', label: 'Sem CPF', icon: NoCpfIcon,
    color: '#d81b60', getCount: r => r.semCpf,
    csvTipo: 'sem-cpf', description: 'Cidadaos sem CPF cadastrado no PEC',
    hasIneFilter: true,
  },
  {
    key: 'semacs', label: 'Sem ACS', icon: NoAcsIcon,
    color: '#fbc02d', getCount: r => r.semAcs,
    csvTipo: 'sem-acs', description: 'Vinculados a equipe mas sem microarea/ACS',
    hasIneFilter: true,
  },
  {
    key: 'semvisita', label: 'Sem Visita (5m)', icon: NoVisitIcon,
    color: '#7b1fa2', getCount: r => r.semVisitaRecente,
    csvTipo: 'sem-visita', description: 'Sem visita domiciliar nos ultimos 5 meses',
    hasIneFilter: true,
  },
  {
    key: 'profissionais', label: 'PEC/SCNES', icon: BadgeIcon,
    color: '#1565c0', getCount: r => r.profissionaisInvalidos,
    csvTipo: 'profissionais', description: 'Profissionais no PEC nao encontrados no SCNES',
    hasIneFilter: true,
  },
  {
    key: 'inativos', label: 'Inativos c/ Atend.', icon: InactiveIcon,
    color: '#00695c', getCount: r => 0,
    csvTipo: 'inativos', description: 'Receberam atendimento mas cadastro inativo',
    hasIneFilter: true,
  },
];

const SaudeQualidadeDados: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumo, setResumo] = useState<ResumoQualidade | null>(null);

  // Equipes for filter
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [selectedIne, setSelectedIne] = useState<string>('');

  // Detail view
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailPage, setDetailPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [dupTipo, setDupTipo] = useState<'cpf' | 'cns'>('cpf');

  const activeConfig = useMemo(() => CARDS.find(c => c.key === activeCard), [activeCard]);

  const fetchResumo = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await saudeService.getQualidadeResumo(municipioAtivo.id);
      // DWH agora suportado
      setResumo(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados de qualidade');
    } finally {
      setLoading(false);
    }
  }, [municipioAtivo?.id]);

  const fetchEquipes = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      const data = await saudeService.getQualidadeEquipes(municipioAtivo.id);
      setEquipes(data || []);
    } catch {
      // non-blocking
    }
  }, [municipioAtivo?.id]);

  useEffect(() => {
    fetchResumo();
    fetchEquipes();
  }, [fetchResumo, fetchEquipes]);

  const fetchDetail = useCallback(async (card: string, page = 0, ine?: string) => {
    if (!municipioAtivo?.id) return;
    setDetailLoading(true);
    try {
      const p = page + 1;
      const ineVal = ine || undefined;
      let data: any;
      switch (card) {
        case 'duplicados':
          data = await saudeService.getQualidadeDuplicados(municipioAtivo.id, dupTipo, p, 50, ineVal);
          break;
        case 'desvinculados':
          data = await saudeService.getQualidadeDesvinculados(municipioAtivo.id, p);
          break;
        case 'semcpf':
          data = await saudeService.getQualidadeSemCpf(municipioAtivo.id, p, 50, ineVal);
          break;
        case 'semacs':
          data = await saudeService.getQualidadeSemAcs(municipioAtivo.id, p, 50, ineVal);
          break;
        case 'semvisita':
          data = await saudeService.getQualidadeSemVisita(municipioAtivo.id, 5, p, 50, ineVal);
          break;
        case 'profissionais':
          data = await saudeService.getQualidadeProfInvalidos(municipioAtivo.id, p, 50, ineVal);
          break;
        case 'inativos':
          data = await saudeService.getQualidadeInativos(municipioAtivo.id, p, 50, ineVal);
          break;
      }
      setDetailData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar detalhes');
    } finally {
      setDetailLoading(false);
    }
  }, [municipioAtivo?.id, dupTipo]);

  const handleCardClick = (key: string) => {
    setActiveCard(key);
    setDetailPage(0);
    setSearchTerm('');
    setSelectedIne('');
    fetchDetail(key, 0, '');
  };

  const handleBack = () => {
    setActiveCard(null);
    setDetailData(null);
    setSelectedIne('');
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setDetailPage(newPage);
    if (activeCard) fetchDetail(activeCard, newPage, selectedIne);
  };

  const handleIneChange = (ine: string) => {
    setSelectedIne(ine);
    setDetailPage(0);
    if (activeCard) fetchDetail(activeCard, 0, ine);
  };

  const handleExport = async (csvTipo: string) => {
    if (!municipioAtivo?.id) return;
    try {
      await saudeService.exportarQualidadeCsv(municipioAtivo.id, csvTipo);
    } catch {
      setError('Erro ao exportar CSV');
    }
  };

  // Filter rows client-side by search term
  const filteredRows = detailData?.registros?.filter((r: any) => {
    if (!searchTerm) return true;
    const term = searchTerm.toUpperCase();
    return Object.values(r).some(v =>
      v && String(v).toUpperCase().includes(term)
    );
  }) || [];

  if (!municipioAtivo) {
    return <Box sx={{ p: 3 }}><Alert severity="warning">Nenhum municipio selecionado</Alert></Box>;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeCard && (
              <IconButton onClick={handleBack} size="small">
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {activeCard ? activeConfig?.label : 'Qualidade de Dados'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeCard ? activeConfig?.description : `${municipioAtivo.nome} - Auditoria de cadastros e-SUS`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeCard && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport(activeConfig?.csvTipo || '')}
              >
                CSV
              </Button>
            )}
            <IconButton onClick={fetchResumo} size="small" title="Atualizar">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Fade>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Summary Cards */}
      {!activeCard && (
        <Fade in timeout={800}>
          <Box>
            {loading ? (
              <Grid container spacing={2}>
                {CARDS.map(c => (
                  <Grid item xs={12} sm={6} md={4} key={c.key}>
                    <Skeleton variant="rounded" height={140} />
                  </Grid>
                ))}
              </Grid>
            ) : resumo ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Chip
                    label={`Total de cidadaos ativos: ${resumo.totalCidadaos.toLocaleString('pt-BR')}`}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Atualizado: ${new Date(resumo.timestamp).toLocaleString('pt-BR')}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Grid container spacing={2}>
                  {CARDS.map(card => {
                    const count = card.getCount(resumo);
                    const pct = resumo.totalCidadaos > 0
                      ? ((count / resumo.totalCidadaos) * 100).toFixed(1)
                      : '0';
                    const Icon = card.icon;
                    const showPct = card.key !== 'inativos' && card.key !== 'profissionais';
                    return (
                      <Grid item xs={12} sm={6} md={4} key={card.key}>
                        <Card
                          sx={{
                            borderLeft: `4px solid ${card.color}`,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                          }}
                        >
                          <CardActionArea onClick={() => handleCardClick(card.key)} sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                width: 48, height: 48, borderRadius: 2,
                                backgroundColor: card.color + '18',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <Icon sx={{ color: card.color, fontSize: 28 }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>
                                  {card.key === 'inativos' ? '...' : count.toLocaleString('pt-BR')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {card.label}
                                </Typography>
                              </Box>
                              {showPct && (
                                <Chip
                                  label={`${pct}%`}
                                  size="small"
                                  sx={{ backgroundColor: card.color + '18', color: card.color, fontWeight: 600 }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {card.description}
                            </Typography>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Summary insights */}
                <Card sx={{ mt: 3, p: 2, backgroundColor: theme.palette.mode === 'dark' ? '#1a2332' : '#f5f7fa' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Diagnostico Rapido
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {resumo.duplicados.cpf > 0 && (
                      <Chip color="error" size="small" label={`${resumo.duplicados.cpf} CPFs duplicados`} />
                    )}
                    {resumo.duplicados.cns > 0 && (
                      <Chip color="error" size="small" label={`${resumo.duplicados.cns} CNS duplicados`} />
                    )}
                    {resumo.desvinculados > 0 && (
                      <Chip color="warning" size="small" label={`${resumo.desvinculados} sem equipe`} />
                    )}
                    {resumo.semCpf > 0 && (
                      <Chip color="error" size="small" label={`${resumo.semCpf} sem CPF`} />
                    )}
                    {resumo.semAcs > 0 && (
                      <Chip color="warning" size="small" label={`${resumo.semAcs} sem ACS`} />
                    )}
                    {resumo.duplicados.cpf === 0 && resumo.duplicados.cns === 0 &&
                     resumo.desvinculados === 0 && resumo.semAcs === 0 && resumo.semCpf === 0 && (
                      <Chip color="success" size="small" label="Cadastros em boa qualidade!" />
                    )}
                  </Box>
                </Card>
              </>
            ) : null}
          </Box>
        </Fade>
      )}

      {/* Detail Table View */}
      {activeCard && (
        <Fade in timeout={600}>
          <Box>
            {/* Controls */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {activeCard === 'duplicados' && (
                <ToggleButtonGroup
                  value={dupTipo}
                  exclusive
                  onChange={(_, v) => { if (v) { setDupTipo(v); setDetailPage(0); } }}
                  size="small"
                >
                  <ToggleButton value="cpf">CPF</ToggleButton>
                  <ToggleButton value="cns">CNS</ToggleButton>
                </ToggleButtonGroup>
              )}

              {/* Equipe filter */}
              {activeConfig?.hasIneFilter && equipes.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Equipe</InputLabel>
                  <Select
                    value={selectedIne}
                    label="Equipe"
                    onChange={e => handleIneChange(e.target.value)}
                  >
                    <MenuItem value="">Todas as equipes</MenuItem>
                    {equipes.map(eq => (
                      <MenuItem key={eq.ine} value={eq.ine}>
                        {eq.nome} ({eq.ine})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                size="small"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              {detailData && (
                <Chip
                  label={`${detailData.total.toLocaleString('pt-BR')} registros`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>

            {/* Trigger dupTipo fetch */}
            {activeCard === 'duplicados' && (
              <Box sx={{ display: 'none' }}>
                <DupTipoEffect dupTipo={dupTipo} fetchDetail={fetchDetail} setDetailPage={setDetailPage} selectedIne={selectedIne} />
              </Box>
            )}

            {/* Table */}
            {detailLoading ? (
              <Box>
                {[...Array(5)].map((_, i) => <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />)}
              </Box>
            ) : detailData?.registros?.length > 0 ? (
              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 320px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {Object.keys(detailData.registros[0])
                        .filter(k => k !== 'id')
                        .map(key => (
                          <TableCell key={key} sx={{ fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                            {formatHeader(key)}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row: any, idx: number) => (
                      <TableRow key={row.id || idx} hover>
                        {Object.entries(row)
                          .filter(([k]) => k !== 'id')
                          .map(([key, val], ci) => (
                            <TableCell key={ci} sx={{ whiteSpace: 'nowrap' }}>
                              {formatCell(key, val)}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">Nenhum registro encontrado</Alert>
            )}

            {detailData && detailData.total > 50 && (
              <TablePagination
                component="div"
                count={detailData.total}
                page={detailPage}
                onPageChange={handlePageChange}
                rowsPerPage={50}
                rowsPerPageOptions={[50]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            )}
          </Box>
        </Fade>
      )}
      {municipioAtivo && resumo && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(resumo)}
          municipioId={municipioAtivo.id}
          painelId="qualidade-dados"
          manualTrigger
        />
      )}

      <ChatIAButton contexto="saude" dadosContexto={resumo ? JSON.stringify(resumo) : undefined} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </Box>
  );
};

// Helper to re-fetch when dupTipo changes
const DupTipoEffect: React.FC<{
  dupTipo: string;
  fetchDetail: (card: string, page: number, ine?: string) => void;
  setDetailPage: (p: number) => void;
  selectedIne: string;
}> = ({ dupTipo, fetchDetail, setDetailPage, selectedIne }) => {
  useEffect(() => {
    setDetailPage(0);
    fetchDetail('duplicados', 0, selectedIne);
  }, [dupTipo, fetchDetail, setDetailPage, selectedIne]);
  return null;
};

function formatHeader(key: string): string {
  const map: Record<string, string> = {
    nome: 'Nome',
    dt_nascimento: 'Nascimento',
    cpf: 'CPF',
    cns: 'CNS',
    telefone: 'Telefone',
    equipe: 'Equipe',
    microarea: 'Microarea',
    situacao: 'Situacao',
    idade: 'Idade',
    sexo: 'Sexo',
    nome_mae: 'Mae',
    ine: 'INE',
    ultima_visita: 'Ultima Visita',
    motivo: 'Motivo',
    ultimo_atendimento: 'Ult. Atendimento',
    equipe_atendimento: 'Equipe Atend.',
    cns_profissional: 'CNS Prof.',
    cpf_profissional: 'CPF Prof.',
    nu_cbo: 'CBO',
    descricao_cbo: 'Desc. CBO',
    unidade: 'Unidade',
    status_scnes: 'Status SCNES',
    documento: 'Documento',
    dt_nascimento_dup: 'Nascimento',
  };
  return map[key] || key.replace(/_/g, ' ');
}

function formatCell(key: string, val: any): React.ReactNode {
  if (val === null || val === undefined) return '-';
  if (key === 'dt_nascimento' || key === 'ultima_visita' || key === 'ultimo_atendimento') {
    try {
      const d = new Date(val);
      return d.toLocaleDateString('pt-BR');
    } catch {
      return String(val);
    }
  }
  if (key === 'cpf' && val) {
    const s = String(val).padStart(11, '0');
    return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
  }
  if (key === 'situacao') {
    const colors: Record<string, 'success' | 'warning' | 'error'> = {
      ATIVO: 'success', MUDOU: 'warning', OBITO: 'error',
    };
    return <Chip label={val} size="small" color={colors[val] || 'default'} variant="outlined" />;
  }
  if (key === 'motivo') {
    return <Chip label={val} size="small" color="warning" variant="outlined" />;
  }
  if (key === 'status_scnes') {
    return <Chip label={val} size="small" color="error" variant="outlined" />;
  }
  if (key === 'sexo') {
    return val === 'M' ? 'Masc' : val === 'F' ? 'Fem' : val;
  }
  return String(val);
}

export default SaudeQualidadeDados;
